
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Feather, Check, AlertCircle, Loader2, X } from 'lucide-react';
import { supabaseService } from '../supabaseService';
import { Profile } from '../types';

interface PenNameModalProps {
  profile: Profile;
  onComplete: (updatedProfile: Profile) => void;
  onDismiss?: () => void;
}

const PenNameModal: React.FC<PenNameModalProps> = ({ profile, onComplete, onDismiss }) => {
  const [penName, setPenName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (penName.length >= 3 && !penName.toLowerCase().startsWith('user_')) {
        setIsChecking(true);
        try {
          const available = await supabaseService.checkPenNameAvailability(penName);
          setIsAvailable(available);
        } catch (err) {
          console.error("Availability check failed:", err);
        } finally {
          setIsChecking(false);
        }
      } else if (penName.toLowerCase().startsWith('user_')) {
        setIsAvailable(false);
        setError("Pen names cannot start with 'User_'");
      } else {
        setIsAvailable(null);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [penName]);

  const handleDismiss = () => {
    try {
      if (profile?.id) {
        localStorage.setItem(`literally_pen_name_dismissed_${profile.id}`, 'true');
      }
    } catch (e) {}

    if (onDismiss) {
      onDismiss();
    } else {
      onComplete({ ...profile, has_selected_pen_name: true });
    }
  };

  const handleSave = async () => {
    if (!isAvailable || penName.length < 3 || penName.toLowerCase().startsWith('user_')) return;
    
    setIsSaving(true);
    setError(null);
    try {
      if (profile?.id) {
        localStorage.setItem(`literally_pen_name_claimed_${profile.id}`, 'true');
        localStorage.setItem(`literally_pen_name_dismissed_${profile.id}`, 'true');
        localStorage.setItem(`literally_profile_display_name_${profile.id}`, penName);
      }
      const updated = await supabaseService.updateProfile({ 
        display_name: penName,
        has_selected_pen_name: true 
      });
      if (updated) {
        onComplete(updated);
      } else {
        // Fallback optimistic update if DB blocked it (e.g. RLS issues)
        onComplete({ ...profile, display_name: penName, has_selected_pen_name: true });
      }
    } catch (err: any) {
      console.warn("Save pen name DB error, applying local fallback:", err);
      const fallback: Profile = { ...profile, display_name: penName, has_selected_pen_name: true };
      onComplete(fallback);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-950/80 backdrop-blur-xl flex items-center justify-center p-6">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-[40px] p-8 shadow-2xl border border-slate-100 dark:border-slate-800 relative"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-5 right-5 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          title="Skip for now"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-6">
            <Feather size={32} />
          </div>
          
          <h2 className="text-2xl font-bold serif text-slate-900 dark:text-white mb-2">
            Claim Your Pen Name
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-8">
            Every great writer needs a unique identity. Choose yours carefully—it's how the world will know you.
          </p>

          <div className="w-full space-y-4">
            <div className="relative">
              <input 
                type="text"
                value={penName}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                  setPenName(val);
                  if (val.toLowerCase().startsWith('user_')) {
                    setError("Pen names cannot start with 'User_'");
                    setIsAvailable(false);
                  } else {
                    setError(null);
                  }
                }}
                placeholder="e.g. InkyPoet"
                className="w-full px-6 py-4 bg-slate-50 dark:bg-slate-800 border-none rounded-2xl focus:ring-2 focus:ring-indigo-500/20 outline-none dark:text-white font-bold text-center text-lg"
                maxLength={20}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {isChecking ? (
                  <Loader2 size={18} className="text-slate-400 animate-spin" />
                ) : isAvailable === true ? (
                  <Check size={18} className="text-emerald-500" />
                ) : isAvailable === false ? (
                  <AlertCircle size={18} className="text-rose-500" />
                ) : null}
              </div>
            </div>

            {isAvailable === false && (
              <p className="text-xs text-rose-500 font-bold">This pen name is already claimed by another writer.</p>
            )}
            
            {penName.length > 0 && penName.length < 3 && (
              <p className="text-xs text-slate-400 font-bold">Minimum 3 characters required.</p>
            )}

            {error && (
              <p className="text-xs text-rose-500 font-bold">{error}</p>
            )}

            <button 
              onClick={handleSave}
              disabled={!isAvailable || isSaving || penName.length < 3}
              className="w-full py-4 bg-indigo-600 disabled:bg-slate-200 dark:disabled:bg-slate-800 disabled:text-slate-400 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
            >
              {isSaving ? <Loader2 size={20} className="animate-spin" /> : 'Claim Identity'}
            </button>
            
            <button
              onClick={handleDismiss}
              className="w-full py-2 text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
            >
              Skip for now
            </button>

            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-center">
              Only letters, numbers, and underscores allowed.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PenNameModal;
